import React from "react";
import Tooltip from "../../common/Tooltip";
import {
  STAT_GROUPS,
  PASSING_EXTRA_ROWS,
  DISCIPLINE_EXTRA_ROWS,
  SHOOTING_EXTRA_ROWS,
  hasStat,
  rowHasData,
  groupHasData,
  groupHasNonZeroData,
  pairSource,
  keptGoalInMatch,
  matchPresence,
} from "./matchStatGroups";

const safeNumber = (n) =>
  n === null || n === undefined || Number.isNaN(n) ? null : Number(n);

// ---- shared row renderers (existing visual idioms) -------------------------

const ValueRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-[#43526E] text-sm">{label}</span>
    <span className="text-[#222A35] font-bold text-sm">{value}</span>
  </div>
);

// The existing Long-Ball pattern: bar + "succ/total (pct%)". Renders only
// when the total exists; a missing success count counts as 0.
const RatioRow = ({ label, succ, total, color }) => {
  const succVal = succ ?? 0;
  const pct =
    total === 0 ? 0 : Math.max(0, Math.min(100, (succVal / total) * 100));
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-[#43526E] text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
        <span className="text-[#222A35] font-bold text-sm">
          {succVal}/{total} ({pct.toFixed(0)}%)
        </span>
      </div>
    </div>
  );
};

const renderGroupRow = (row, stats) => {
  if (!rowHasData(row, stats)) return null;
  if (row.kind === "pair") {
    // won vs (won+lost) — pairSource already decided new keys vs the legacy
    // fallback pair, never both (brief §1).
    const source = pairSource(row, stats);
    const won = safeNumber(stats[source.wonKey]) ?? 0;
    const lost = safeNumber(stats[source.lostKey]) ?? 0;
    return (
      <RatioRow
        key={row.label}
        label={row.label}
        succ={won}
        total={won + lost}
        color={row.color}
      />
    );
  }
  if (row.kind === "ratio") {
    // Rows whose success count is a NEW provider field degrade to the plain
    // value row on historical data instead of faking "0/n (0%)".
    if (row.valueWhenNoSucc && !hasStat(stats[row.succKey])) {
      return (
        <ValueRow
          key={row.label}
          label={row.label}
          value={safeNumber(stats[row.totalKey])}
        />
      );
    }
    return (
      <RatioRow
        key={row.label}
        label={row.label}
        succ={safeNumber(stats[row.succKey])}
        total={safeNumber(stats[row.totalKey])}
        color={row.color}
      />
    );
  }
  return (
    <ValueRow key={row.label} label={row.label} value={safeNumber(stats[row.key])} />
  );
};

// Shared card chrome (existing card idiom of this view).
const StatCard = ({ title, icon, children }) => (
  <div className="bg-white border border-[#E5E6E8] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-4 border-b border-[#E5E5E5] pb-4">
      {icon}
      <span className="text-[#1A212F] font-bold text-base">{title}</span>
    </div>
    <div className="space-y-3 px-2">{children}</div>
  </div>
);

// ---- icons (reused from the existing cards) --------------------------------

const TargetIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.3346 9.99999C18.3346 14.6024 14.6037 18.3333 10.0013 18.3333M18.3346 9.99999C18.3346 5.39762 14.6037 1.66666 10.0013 1.66666M18.3346 9.99999H15.0013M10.0013 18.3333C5.39893 18.3333 1.66797 14.6024 1.66797 9.99999M10.0013 18.3333V15M1.66797 9.99999C1.66797 5.39762 5.39893 1.66666 10.0013 1.66666M1.66797 9.99999H5.0013M10.0013 1.66666V4.99999"
      stroke="#2F6AF5"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.3346 1.66666L9.16797 10.8333M18.3346 1.66666L12.5013 18.3333L9.16797 10.8333M18.3346 1.66666L1.66797 7.49999L9.16797 10.8333"
      stroke="#2F6AF5"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CompassIcon = (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z"
      stroke="#2F6AF5"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.5346 6.46663L11.768 11.7666L6.46797 13.5333L8.23464 8.23329L13.5346 6.46663Z"
      stroke="#2F6AF5"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = (
  <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.00065 18.3333C8.00065 18.3333 14.6673 15 14.6673 9.99996V4.16663L8.00065 1.66663L1.33398 4.16663V9.99996C1.33398 15 8.00065 18.3333 8.00065 18.3333Z"
      stroke="#FF5355"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldBlueIcon = (
  <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.00065 18.3333C8.00065 18.3333 14.6673 15 14.6673 9.99996V4.16663L8.00065 1.66663L1.33398 4.16663V9.99996C1.33398 15 8.00065 18.3333 8.00065 18.3333Z"
      stroke="#2F6AF5"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GROUP_ICONS = {
  offensive: TargetIcon,
  defensive: ShieldBlueIcon,
  duels: CompassIcon,
  goalkeeping: ShieldBlueIcon,
};

const DetailedStatistics = ({ selectedMatch, isGoalkeeper = false }) => {
  const stats = selectedMatch.statistics || {};

  // 0-minutes case (brief §1): the whole card grid collapses to one quiet
  // line when the player did not play this match.
  const { played } = matchPresence(selectedMatch);

  // Per-match goalkeeper detection for the Goalkeeping card (brief §1):
  // position G in the lineup, or any GK field > 0 — NOT the season position.
  const keptGoal = keptGoalInMatch(stats, selectedMatch.lineup_info);

  // ---- Shooting (bespoke card, kept) — hidden when the match has no shot data
  const shotsTotal = safeNumber(stats.shots);
  const shotsOnT = safeNumber(stats.shots_on_target);
  // Off target is DERIVED (shots − on target). §0.1 audit 2026-08-17: the BE
  // never sends a `shot_off_target` key — the former read of it was dead.
  const shotsOffT =
    shotsTotal !== null && shotsOnT !== null
      ? Math.max(0, shotsTotal - shotsOnT)
      : null;
  const shootingExtraVisible = SHOOTING_EXTRA_ROWS.filter((r) =>
    rowHasData(r, stats)
  );
  const anyShootingExtraNonZero = shootingExtraVisible.some(
    (r) => Number(stats[r.key]) !== 0
  );
  // Keepers: an all-zero shooting card is structural, not information —
  // hide it unless something is actually non-zero (Marin sharpening 2026-08-14).
  const anyShotValue =
    shotsTotal !== null || shotsOnT !== null || shotsOffT !== null;
  const anyShotNonZero = !!(shotsTotal || shotsOnT || shotsOffT);
  const showShooting =
    (anyShotValue || shootingExtraVisible.length > 0) &&
    (!isGoalkeeper || anyShotNonZero || anyShootingExtraNonZero);

  // ---- Passing card rows
  const showKeyPasses = hasStat(stats.key_passes);
  const showPassAccuracy = hasStat(stats.passes);
  const showLongBalls = hasStat(stats.long_balls);
  const passingExtraVisible = PASSING_EXTRA_ROWS.filter((r) =>
    rowHasData(r, stats)
  );
  const showPassing =
    showKeyPasses || showPassAccuracy || showLongBalls || passingExtraVisible.length > 0;

  // ---- Creativity & Control ("Touches" removed 2026-08-14: TheSports does
  // not deliver the field — it was an eternal dash; no intent found in git
  // history for keeping it)
  const showPossLost = hasStat(stats.poss_losts);
  const showCreativity = showKeyPasses || showPossLost;

  // ---- Discipline (formerly "Fouls") rows
  const showFouls = hasStat(stats.fouls);
  const showYellow = hasStat(stats.yellow_cards);
  const showRed = hasStat(stats.red_cards);
  const disciplineExtraVisible = DISCIPLINE_EXTRA_ROWS.filter((r) =>
    rowHasData(r, stats)
  );
  const showDiscipline =
    showFouls || showYellow || showRed || disciplineExtraVisible.length > 0;

  // ---- Generic groups (Offensive / Defensive / Duels / Goalkeeping)
  // The Goalkeeping card is gated on keeping goal IN THIS MATCH (keptGoal);
  // for season keepers the Offensive group additionally needs a NON-ZERO
  // value (no zero graveyard).
  const visibleGroups = STAT_GROUPS.filter(
    (g) =>
      (!g.keeperOnly || keptGoal) &&
      groupHasData(g, stats) &&
      !(isGoalkeeper && g.id === "offensive" && !groupHasNonZeroData(g, stats))
  );

  const nothingToShow =
    !played ||
    (!showShooting &&
      !showPassing &&
      !showCreativity &&
      !showDiscipline &&
      visibleGroups.length === 0);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <svg
          width="19"
          height="18"
          viewBox="0 0 19 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.5996 8.99999H14.3996L11.9996 16.2L7.19961 1.79999L4.79961 8.99999H1.59961"
            stroke="#2283FF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <h3 className="text-[#1A212F] font-bold text-lg">
          Detailed Statistics
        </h3>
        <Tooltip content="Only statistics reported for this match are shown" position="bottom-right">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.79883 12.2V9M8.79883 5.8H8.80683M16.7988 9C16.7988 13.4183 13.2171 17 8.79883 17C4.38055 17 0.798828 13.4183 0.798828 9C0.798828 4.58172 4.38055 1 8.79883 1C13.2171 1 16.7988 4.58172 16.7988 9Z"
              stroke="#A2A2A2"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Tooltip>
      </div>

      {nothingToShow ? (
        <div className="bg-white border border-[#E5E6E8] rounded-xl p-6 text-center">
          <p className="text-gray-500 text-sm">
            No detailed statistics available for this match
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {/* Shooting (bespoke visual, kept as-is) */}
          {showShooting && (
            <StatCard title="Shooting" icon={TargetIcon}>
              {anyShotValue && (
                <div>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="text-[#43526E] text-sm">Shots</span>
                    <span className="text-[#222A35] font-bold text-sm">
                      {shotsTotal !== null
                        ? shotsTotal
                        : (shotsOnT || 0) + (shotsOffT || 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {shotsTotal === null || shotsTotal === 0 || shotsOnT === null ? (
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" />
                    ) : (
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#38AB75] rounded-full"
                          style={{
                            width: `${Math.max(
                              0,
                              Math.min(100, (shotsOnT / shotsTotal) * 100)
                            )}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {/* Empty rule: a sub-value the provider did not send renders
                      nothing — no dash placeholders. */}
                  {(shotsOnT !== null || shotsOffT !== null) && (
                    <div className="flex items-center justify-between mt-1">
                      {shotsOnT !== null && (
                        <span className="text-[#43526E] text-[10px]">
                          On Target: {shotsOnT}
                        </span>
                      )}
                      {shotsOffT !== null && (
                        <span className="text-[#43526E] text-[10px]">
                          Off Target: {shotsOffT}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Shot Accuracy */}
              {shotsTotal !== null && shotsOnT !== null && (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-[#43526E] text-sm">Shot Accuracy</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#2283FF] rounded-full"
                        style={{
                          width: `${
                            shotsTotal === 0
                              ? 0
                              : Math.max(
                                  0,
                                  Math.min(100, (shotsOnT / shotsTotal) * 100)
                                )
                          }%`,
                        }}
                      />
                    </div>
                    <span className="text-[#222A35] font-bold text-sm">
                      {(shotsTotal === 0
                        ? 0
                        : Math.max(
                            0,
                            Math.min(100, (shotsOnT / shotsTotal) * 100)
                          )
                      ).toFixed(0)}
                      %
                    </span>
                  </div>
                </div>
              )}

              {/* Brief §1: shots_ibox / shots_obox / big chances (value rows) */}
              {shootingExtraVisible.map((row) => renderGroupRow(row, stats))}
            </StatCard>
          )}

          {/* Passing (existing card + Crosses ratio) */}
          {showPassing && (
            <StatCard title="Passing" icon={SendIcon}>
              {showKeyPasses && (
                <ValueRow
                  label="Key Passes"
                  value={safeNumber(stats.key_passes)}
                />
              )}
              {showPassAccuracy && (
                <RatioRow
                  label="Pass Accuracy"
                  succ={safeNumber(stats.passes_accuracy)}
                  total={safeNumber(stats.passes)}
                  color="#2283FF"
                />
              )}
              {showLongBalls && (
                <RatioRow
                  label="Long Ball Accuracy"
                  succ={safeNumber(stats.long_balls_accuracy)}
                  total={safeNumber(stats.long_balls)}
                  color="#F2994A"
                />
              )}
              {passingExtraVisible.map((row) => renderGroupRow(row, stats))}
            </StatCard>
          )}

          {/* Generic groups: Offensive / Defensive / Duels / Goalkeeping */}
          {visibleGroups.map((group) => (
            <StatCard
              key={group.id}
              title={group.title}
              icon={GROUP_ICONS[group.id] || TargetIcon}
            >
              {group.rows.map((row) => renderGroupRow(row, stats))}
            </StatCard>
          ))}

          {/* Creativity & Control (Touches removed — field is never delivered) */}
          {showCreativity && (
            <StatCard title="Creativity & Control" icon={CompassIcon}>
              {showKeyPasses && (
                <ValueRow
                  label="Key Passes"
                  value={safeNumber(stats.key_passes)}
                />
              )}
              {showPossLost && (
                <ValueRow
                  label="Possession Lost"
                  value={safeNumber(stats.poss_losts)}
                />
              )}
            </StatCard>
          )}

          {/* Discipline (formerly Fouls) */}
          {showDiscipline && (
            <StatCard title="Discipline" icon={ShieldIcon}>
              {showFouls && (
                <ValueRow
                  label="Fouls Committed"
                  value={safeNumber(stats.fouls)}
                />
              )}
              {disciplineExtraVisible.map((row) => renderGroupRow(row, stats))}
              {showYellow && (
                <div className="flex items-center justify-between">
                  <span className="text-[#43526E] text-sm">Yellow Cards</span>
                  <div className="flex items-center gap-1.5 bg-[#FFC6541A] px-2 py-1 rounded-lg">
                    <div
                      className=" w-4 h-5 rounded-sm"
                      style={{
                        background:
                          "linear-gradient(180deg, #FFC654 0%, #FFDE9C 100%)",
                      }}
                    ></div>
                    <span className="text-[#1A212F] font-semibold text-sm">
                      {safeNumber(stats.yellow_cards)}
                    </span>
                  </div>
                </div>
              )}
              {showRed && (
                <div className="flex items-center justify-between">
                  <span className="text-[#43526E] text-sm">Red Cards</span>
                  <div className="flex items-center gap-1.5 bg-[#EF44441A] px-2 py-1 rounded-lg">
                    <div
                      className="w-4 h-5 rounded-sm"
                      style={{
                        background:
                          "linear-gradient(180deg, #FF4D4F 0%, #FF8183 100%)",
                      }}
                    ></div>
                    <span className="text-[#1A212F] font-semibold text-sm">
                      {safeNumber(stats.red_cards)}
                    </span>
                  </div>
                </div>
              )}
            </StatCard>
          )}
        </div>
      )}
    </div>
  );
};

export default DetailedStatistics;
